import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { REEL, type ReelItem } from '@/lib/reel';
import type { StudioId } from '@/lib/studios';

// MADE HERE (Studio Blueprint 02, home screen 2): real outputs, each labelled
// with the studio that made it; a tap opens it full screen in the Reel
// (/watch). Owner 2026-10-16: every item plays MOVING, and nothing is cut off
// at the edge: a full grid from tablet up, a swipe row on phones. With
// ?door=<studio> the studio's outputs come first, and a family visitor
// (storybook) never sees corrido.

export function MadeHere({ door, skip = [] }: { door: StudioId | null; skip?: readonly string[] }) {
  const { t } = useTranslation();
  // `skip`: outputs another part of the page already shows (home's stage
  // cards), so nothing appears twice (owner 2026-10-16). /watch keeps them all.
  let items: ReelItem[] = REEL.filter((r) => !skip.includes(r.id));
  if (door === 'storybook') items = items.filter((r) => r.family);
  else if (door) items.sort((a, b) => Number(b.studio === door) - Number(a.studio === door));

  return (
    <section aria-labelledby="made-here" data-testid="made-here" className="py-10 sm:py-14">
      <div className="container mx-auto max-w-6xl px-4 md:px-8 xl:px-4">
        <div className="flex items-end justify-between gap-4">
          <div>
            <p className="eyebrow">{t('home.madeEyebrow')}</p>
            <h2 id="made-here" className="mt-2 font-display text-[1.6rem] font-bold leading-tight sm:text-[2.2rem]">{t('home.madeTitle')}</h2>
          </div>
          <Link to="/watch" className="hidden shrink-0 rounded-full border border-white/15 px-4 py-2 text-[14px] font-semibold text-foreground hover:border-primary/50 sm:inline-flex">
            {t('home.madeAll')}
          </Link>
        </div>
        <ul className="-mx-4 mt-5 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2 [scrollbar-width:none] sm:mx-0 sm:grid sm:grid-cols-3 sm:overflow-visible sm:px-0 lg:grid-cols-4 [&::-webkit-scrollbar]:hidden">
          {items.map((item) => (
            <li key={item.id} className="w-[44vw] max-w-[13rem] shrink-0 snap-start sm:w-auto sm:max-w-none">
              <Link to={`/watch?item=${item.id}`} data-testid={`made-${item.id}`} className="group block">
                <span className="relative block aspect-[9/16] overflow-hidden rounded-2xl border border-white/10 bg-black">
                  {item.kind === 'video' ? (
                    <video src={item.src} poster={item.poster} autoPlay muted loop playsInline preload="metadata" aria-hidden className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                  ) : (
                    <img src={item.poster} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                  )}
                  <span aria-hidden className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/85 to-transparent" />
                  <span className="absolute bottom-2 left-2 right-2 font-mono text-[10.5px] uppercase tracking-[0.12em] text-white/80">
                    {t(`studios.${item.studio}.name`)}
                  </span>
                </span>
                <span className="mt-2 block text-[14px] font-semibold leading-snug text-foreground">{t(`reel.items.${item.key}`)}</span>
              </Link>
            </li>
          ))}
        </ul>
        <p className="mt-2 text-[12.5px] text-muted-foreground sm:hidden">{t('home.madeSwipe')}</p>
        <div className="mt-3 sm:hidden">
          <Link to="/watch" className="inline-flex min-h-[44px] items-center text-[14px] font-semibold text-primary">
            {t('home.madeAll')} →
          </Link>
        </div>
      </div>
    </section>
  );
}
